import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import type { DragEndEvent } from "@dnd-kit/core"
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable"
import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import {
	colorError,
	colorInfo,
	radiusMd,
	spacingStaticSm,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import { newsImageConfig } from "@slfinrtl/news-service-routes"
import type { NewsImageView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useRef, useState } from "react"
import type { FileRejection } from "react-dropzone"
import { ErrorCode, useDropzone } from "react-dropzone"
import { useController, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { FieldWrapper, Tooltip } from "src/components"
import { useNewsImageUpload } from "../hooks/useNewsImageUpload/useNewsImageUpload"
import { NewsImageAddTile } from "./NewsImageAddTile"
import { NewsImagePlaceholderTile } from "./NewsImagePlaceholderTile"
import { NewsImageTile } from "./NewsImageTile"
import { newsEditorMessages as messages } from "./newsEditor.messages"
import type { NewsImageTileState } from "./newsImageTiles"
import { reorderTiles, toImageIds } from "./newsImageTiles"

const { allowedContentTypes, maxImagesPerNewsItem, maxSizeInBytes } = newsImageConfig
const bytesPerMb = 1024 * 1024
const accept = Object.fromEntries(allowedContentTypes.map((contentType) => [contentType, []]))

interface NewsImagesFieldProps {
	/** shows the images without the possibility to change them */
	disabledHint?: string
	/** the saved images the tiles start with */
	images: NewsImageView[]
	/** name of the form field that receives the ids of the images in the order they are shown */
	name: string
	/** receives the preview url of the first image, `undefined` if there is none */
	onFirstImageUrlChange?: (url: string | undefined) => void
	onUploadingChange: (isUploading: boolean) => void
}

export const NewsImagesField = ({
	disabledHint,
	images,
	name,
	onFirstImageUrlChange,
	onUploadingChange,
}: NewsImagesFieldProps) => {
	const { control } = useFormContext()
	const { formatMessage } = useIntl()
	const upload = useNewsImageUpload()
	const {
		field: { onChange },
		fieldState: { error: fieldError },
	} = useController({ control, name })
	const isReadOnly = !!disabledHint

	const [tiles, setTiles] = useState<NewsImageTileState[]>(() =>
		images.map(({ id, thumbnailUrl }) => ({ id, key: id, previewUrl: thumbnailUrl, status: "done" })),
	)
	const [error, setError] = useState<string>()
	const tilesRef = useRef(tiles)
	const objectUrlsRef = useRef(new Set<string>())
	const uploadCounterRef = useRef(0)
	const isMountedRef = useRef(false)

	useEffect(() => {
		isMountedRef.current = true
		const objectUrls = objectUrlsRef.current
		return () => {
			isMountedRef.current = false
			for (const objectUrl of objectUrls) {
				URL.revokeObjectURL(objectUrl)
			}
		}
	}, [])

	const isUploading = tiles.some(({ status }) => status === "uploading")
	useEffect(() => onUploadingChange(isUploading), [isUploading, onUploadingChange])

	const firstImageUrl = tiles[0]?.previewUrl
	useEffect(() => onFirstImageUrlChange?.(firstImageUrl), [firstImageUrl, onFirstImageUrlChange])

	const commit = useCallback(
		(next: NewsImageTileState[]) => {
			tilesRef.current = next
			setTiles(next)
			onChange(toImageIds(next))
		},
		[onChange],
	)

	const revokePreview = (previewUrl: string | undefined) => {
		if (previewUrl && objectUrlsRef.current.delete(previewUrl)) {
			URL.revokeObjectURL(previewUrl)
		}
	}

	const finishUpload = (key: string, id: string) => {
		if (isMountedRef.current) {
			commit(tilesRef.current.map((tile) => (tile.key === key ? { ...tile, id, status: "done" } : tile)))
		}
	}

	const updateProgress = (key: string, fraction: number) => {
		const progress = Math.min(100, Math.max(0, Math.round(fraction * 100)))
		const tile = tilesRef.current.find((candidate) => candidate.key === key)
		if (!isMountedRef.current || tile?.status !== "uploading" || tile.progress === progress) {
			return
		}
		tilesRef.current = tilesRef.current.map((candidate) =>
			candidate.key === key ? { ...candidate, progress } : candidate,
		)
		setTiles(tilesRef.current)
	}

	const failUpload = (key: string, fileName: string) => {
		if (!isMountedRef.current) {
			return
		}
		revokePreview(tilesRef.current.find((tile) => tile.key === key)?.previewUrl)
		commit(tilesRef.current.filter((tile) => tile.key !== key))
		setError(formatMessage(messages.imageUploadFailed, { name: fileName }))
	}

	const getRejectionMessage = (rejection: FileRejection | undefined) => {
		switch (rejection?.errors[0]?.code) {
			case ErrorCode.FileInvalidType:
				return formatMessage(messages.imageInvalidType)
			case ErrorCode.FileTooLarge:
				return formatMessage(messages.imageTooLarge, { maxSizeInMb: maxSizeInBytes / bytesPerMb })
			default:
				return undefined
		}
	}

	// react-dropzone would still accept as many files as there are free slots, but none must be taken then
	const onDrop = (acceptedFiles: File[], rejections: FileRejection[]) => {
		if (acceptedFiles.length + rejections.length > maxImagesPerNewsItem - tilesRef.current.length) {
			setError(formatMessage(messages.imageTooMany, { maxImages: maxImagesPerNewsItem }))
			return
		}
		setError(getRejectionMessage(rejections[0]))
		const newTiles = acceptedFiles.map((file): NewsImageTileState => {
			const previewUrl = URL.createObjectURL(file)
			objectUrlsRef.current.add(previewUrl)
			return { key: `upload-${++uploadCounterRef.current}`, previewUrl, progress: 0, status: "uploading" }
		})
		if (newTiles.length === 0) {
			return
		}
		commit([...tilesRef.current, ...newTiles])
		acceptedFiles.forEach((file, index) => {
			upload(file, (fraction) => updateProgress(newTiles[index].key, fraction))
				.then((id) => finishUpload(newTiles[index].key, id))
				.catch(() => failUpload(newTiles[index].key, file.name))
		})
	}

	const freeSlots = maxImagesPerNewsItem - tiles.length
	const { getInputProps, getRootProps, isDragActive, isDragReject, open } = useDropzone({
		accept,
		disabled: isReadOnly,
		// makes dragging more files than free slots over the field a reject, 0 means no limit and is handled below
		maxFiles: freeSlots,
		maxSize: maxSizeInBytes,
		noClick: true,
		noKeyboard: true,
		onDrop,
	})

	const isDropRejected = isDragReject || freeSlots <= 0

	const sensors = useSensors(useSensor(PointerSensor))
	const onDragEnd = ({ active, over }: DragEndEvent) => {
		const next = reorderTiles(tilesRef.current, String(active.id), over ? String(over.id) : undefined)
		if (next !== tilesRef.current) {
			commit(next)
		}
	}

	const onDelete = (key: string) => {
		revokePreview(tilesRef.current.find((tile) => tile.key === key)?.previewUrl)
		setError(undefined)
		commit(tilesRef.current.filter((tile) => tile.key !== key))
	}

	const errorMessage = fieldError?.message ?? error

	return (
		<FieldWrapper
			disabled={isReadOnly}
			error={errorMessage ? { message: errorMessage } : undefined}
			label={formatMessage(messages.images)}
		>
			<Description $isDisabled={isReadOnly} aria-disabled={isReadOnly ? true : undefined}>
				<PText size="xs" color="contrast-medium">
					{formatMessage(messages.imagesDescription, { maxImages: maxImagesPerNewsItem })}
				</PText>
			</Description>
			<Tooltip content={disabledHint}>
				<Area
					{...(isReadOnly ? {} : { ...getRootProps(), tabIndex: undefined })}
					$dropState={isDragActive ? (isDropRejected ? "rejected" : "accepted") : undefined}
					data-testid="news-images-dropzone"
				>
					{!isReadOnly && <input {...getInputProps()} data-testid="news-images-input" />}
					<DndContext sensors={sensors} onDragEnd={onDragEnd}>
						<SortableContext items={tiles.map(({ key }) => key)} strategy={rectSortingStrategy}>
							<Grid>
								{tiles.map(({ key, previewUrl, progress, status }, index) => (
									<NewsImageTile
										key={key}
										alt={formatMessage(messages.imageAlt, {
											position: index + 1,
											total: tiles.length,
										})}
										deleteLabel={formatMessage(messages.deleteImage)}
										isDisabled={isReadOnly}
										isUploading={status === "uploading"}
										previewUrl={previewUrl}
										progress={progress}
										sortableId={key}
										uploadingLabel={formatMessage(messages.uploadingImage)}
										onDelete={() => onDelete(key)}
									/>
								))}
								{!isReadOnly &&
									Array.from({ length: Math.max(freeSlots, 0) }, (_, index) =>
										index === 0 ? (
											<NewsImageAddTile
												key="add"
												label={formatMessage(messages.uploadImages)}
												onClick={open}
											/>
										) : (
											<NewsImagePlaceholderTile key={`placeholder-${index}`} />
										),
									)}
							</Grid>
						</SortableContext>
					</DndContext>
				</Area>
			</Tooltip>
		</FieldWrapper>
	)
}

// pulls the description up to the label, like the description of the pds input fields
const Description = styled("div", { shouldForwardProp })<{ $isDisabled: boolean }>`
	margin-block-start: calc(-1 * ${spacingStaticXs});
	opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};
`

const Area = styled("div", { shouldForwardProp })<{ $dropState: "accepted" | "rejected" | undefined }>`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
	border-radius: ${radiusMd};
	cursor: ${({ $dropState }) => ($dropState === "rejected" ? "no-drop" : "default")};
	outline-style: ${({ $dropState }) => ($dropState ? "dashed" : "none")};
	outline-width: 2px;
	outline-color: ${({ $dropState }) => ($dropState === "rejected" ? colorError : colorInfo)};
	outline-offset: 2px;
	/* keeps the drag border clear of the description above */
	margin-block-start: ${spacingStaticSm};

	/* the hidden file input would otherwise be a flex item and add a gap above the tiles */
	> input {
		position: absolute;
	}
`

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(${maxImagesPerNewsItem}, minmax(0, 1fr));
	gap: ${spacingStaticSm};

	&:empty {
		display: none;
	}
`
